import type { FormulationRelations, IDescriptor } from "./descriptor";
import { isRealDescriptor } from "./descriptor";

/**
 * Descriptors may include LaTEX. If they do, we should format them. If not,
 * we should NOT format them because LaTEX converts all text into this ugly
 * cursive font and removes all the spaces
 * @param descriptor_key
 */
// we don't currently have a need for this but leaving it in for when we need
// to display formulas
// export function formatDescriptorKey(descriptor_key: string) {
//   if (descriptor_key.indexOf("{") !== -1) {
//     // HACK: if a descriptor contains a bracket, it is likely LATeX
//     return (
//       <Formula src={descriptor_key} displayText={displayKey(descriptor_key)} />
//     );
//   }
//   return displayKey(descriptor_key);
// }

/**
 * Format a delimited string by:
 *
 * 1. Remove repeated segments
 * 2. Replace one delimiter with another
 *
 * @param {string} descriptorKey - the string to format
 * @param {string} inDelim - the delimiter of the input string
 * @param {string} outDelim - the delimiter of the output string
 *
 * @returns {string}
 */
export function displayKey(
  descriptorKey: string,
  inDelim: string = "~",
  outDelim: string = ": ",
): string {
  // split on delim
  const keySegments = descriptorKey.split(inDelim);

  // remove duplicates
  const dedupedKeySegments = keySegments
    .filter((segment) => !!segment)
    .reduce((arr: string[], str: string) => {
      if (arr.length === 0 || str !== arr[arr.length - 1]) {
        arr.push(str);
      }
      return arr;
    }, []);

  // re-assemble
  return dedupedKeySegments.join(outDelim);
}

/**
 * Takes an axis name, replaces any `~` characters with `:`, and then appends
 * parenthesized units if the units exist and are not `"dimensionless"`.
 *
 * Useful for exporting plots because plotly does not accept react components
 * for things like title and axis labels.
 */
export function formatDescriptorKeyWithUnits(name: string, units?: string) {
  return `${displayKey(name)}${
    units && units !== "dimensionless" ? ` (${units})` : ""
  }`;
}

export function toDescriptorKeyDropdownOption(
  descriptorOrKey: IDescriptor | string,
  formattedUnits?: string,
) {
  const key =
    typeof descriptorOrKey === "string"
      ? descriptorOrKey
      : descriptorOrKey.descriptor_key;
  return {
    value: key,
    label: formatDescriptorKeyWithUnits(key, formattedUnits),
  };
}

/**
 * if a descriptor has units, return them as parentheses after the descriptor key
 * @param {IDescriptor} descriptor any descriptor
 * @param {string} inDelim - the delimiter of the input string
 * @param {string} outDelim - the delimiter of the output string
 */
export function descriptorNameWithUnits(
  descriptor: IDescriptor,
  inDelim: string = "~",
  outDelim: string = ":",
) {
  const formattedDescriptorKey = displayKey(
    descriptor.descriptor_key,
    inDelim,
    outDelim,
  );
  if (!isRealDescriptor(descriptor) || !descriptor.units) {
    return formattedDescriptorKey;
  }

  return `${formattedDescriptorKey} (${descriptor.units})`;
}

export function labelsMapForIngredients(
  selectedIngredients: Set<string>,
  relations: FormulationRelations,
): Partial<Record<string, string[]>> {
  return Object.fromEntries(
    Object.entries(relations.labels)
      .map(([label, ingredients]) => [
        label,
        ingredients?.filter((ingredient) =>
          selectedIngredients.has(ingredient),
        ),
      ])
      .filter(([, ingredients]) => ingredients?.length),
  );
}
